// StaffTable.jsx - добавьте кнопку возврата и обработку isAggregatedView
import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faSearch,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';

import './StaffTable.css';

const cor2Float = (a)=>parseFloat(a.toFixed(2));

const StaffTable = ({
  data = [],
  columnsConfig = [],
  onSearch,
  onKindergartenSelect,
  onRowClick,
  onBackToAggregated, // Новый проп для возврата к агрегированному виду
  kindergartens = [],
  selectedKindergarten = '',
  loading = false,
  isAggregatedView = false
}) => {

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');

  // Конфигурация столбцов по умолчанию
  const defaultColumns = [
    {
      key: 'positionName',
      label: 'Найменування посади',
      sortable: true,
      width: '40%'
    },
    {
      key: 'totalEmployees',
      label: 'Фактично у штаті',
      sortable: true,
      width: '15%'
    },
    {
      key: 'vacantUnits',
      label: 'Вакантних місць',
      sortable: true,
      width: '15%'
    },
    {
      key: 'totalUnits',
      label: 'Всього',
      sortable: true,
      width: '15%'
    }
  ];

  const columns = columnsConfig.length > 0 ? columnsConfig : defaultColumns;

  // Обробка даних
  const processedData = useMemo(() => {
    return data.map(item => ({
      ...item,
      // Для зворотної сумісності
      actualStaff: item.totalEmployees || 0,
      vacantPositions: item.vacantUnits || 0,
      totalPositions: item.totalUnits || 0
    }));
  }, [data]);

  // Функция сортировки
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Фільтрація даних за пошуком
  const filteredData = useMemo(() => {
    if (!searchTerm) return processedData;
    
    const term = searchTerm.toLowerCase();
    return processedData.filter(item => 
      item.positionName?.toLowerCase().includes(term) ||
      (item.positionType && item.positionType.toLowerCase().includes(term)) ||
      (item.kindergartenName && item.kindergartenName.toLowerCase().includes(term)) ||
      (item.kindergartenNumber && item.kindergartenNumber.toString().includes(term))
    );
  }, [processedData, searchTerm]);

  // Сортированные данные
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleKindergartenChange = (e) => {
    if (onKindergartenSelect) {
      onKindergartenSelect(e.target.value);
    }
  };

  const handleRowClick = (item) => {
    if (onRowClick && item.kindergartenId) {
      onRowClick({
        kindergartenId: item.kindergartenId,
        kindergartenNumber: item.kindergartenNumber,
        kindergartenName: item.kindergartenName
      });
    }
  };

  const handleBackToAggregated = () => {
    if (onBackToAggregated) {
      onBackToAggregated();
    }
  };

  // Розрахунок загальної статистики
  const totalStats = useMemo(() => {
    if (isAggregatedView) {
      return {
        totalPositions: sortedData.reduce((sum, item) => sum + (
          item.totals?.pedagogical.total + 
          item.totals?.technical.total + 
          item.totals?.medical.total + 
          item.totals?.service.total || 0
        ), 0),
        totalEmployees: 0,
        totalVacant: sortedData.reduce((sum, item) => sum + (
          item.totals?.pedagogical.vacant + 
          item.totals?.technical.vacant + 
          item.totals?.medical.vacant + 
          item.totals?.service.vacant || 0
        ), 0)
      };
    } else {
      return {
        totalPositions: sortedData.reduce((sum, item) => sum + (item.totalUnits || 0), 0),
        totalEmployees: sortedData.reduce((sum, item) => sum + (item.totalEmployees || 0), 0),
        totalVacant: sortedData.reduce((sum, item) => sum + (item.vacantUnits || 0), 0)
      };
    }
  }, [sortedData, isAggregatedView]);

  if (loading) {
    return (
      <div className="staff-table-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Завантаження даних...</span>
          </div>
          <p className="text-muted mt-2">Завантаження штатного розкладу...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="staff-table-container">
      {/* Кнопка возврата к агрегированному виду (показывается только в детальном просмотре) */}
      {!isAggregatedView && selectedKindergarten && (
        <div className="back-to-aggregated-section mb-3">
          <button 
            className="btn btn-back-to-aggregated  compact" 
            onClick={handleBackToAggregated}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="back-arrow-icon" />
            Повернутись до списку садочків
          </button>
        </div>
      )}

      {/* Панель пошуку та вибору садочка (только для агрегированного просмотра) */}
      {isAggregatedView && (data.length > 0) && (
        <div className="staff-toolbar">
          {false && <div className="staff-kindergarten-selector">
            <label htmlFor="kindergarten-select" className="staff-label">
              Садочок:
            </label>
            <select
              id="kindergarten-select"
              className="staff-form-control"
              value={selectedKindergarten}
              onChange={handleKindergartenChange}
            >
              <option value="">{kindergartens.length === 0 ? 'Оберіть спочатку район' : 'Всі садочки району'}</option>
              {kindergartens.map((kg, index) => (
                <option key={kg.id} value={kg.id}>
                  {kg.name}
                </option>
              ))}
            </select>
          </div>}
          
          <div className="staff-search">
            <div className="staff-search-input-wrapper">
              <FontAwesomeIcon icon={faSearch} className="staff-search-icon" />
              <input
                type="text"
                className="staff-form-control staff-search-input"
                placeholder={isAggregatedView ? "Пошук за номером або назвою садочка..." : "Пошук за назвою посади або типом..."}
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* Заголовок таблицы для детального просмотра */}
      {!isAggregatedView && selectedKindergarten && (
        <div className="detail-view-header mb-3">
          <h5 className="detail-view-title">
            Детальний перегляд посад для садочка
          </h5>
          {kindergartens.find(kg => kg.id === selectedKindergarten) && (
            <p className="detail-view-subtitle text-muted">
              {kindergartens.find(kg => kg.id === selectedKindergarten)?.name}
            </p>
          )}
        </div>
      )}

      {/* Таблиця */}
      {sortedData.length > 0 ? (
        <>
          <div className="table-responsive">
            <table className="table align-middle staff-table" style={{ fontSize: '14px' }}>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th 
                      key={column.key} 
                      style={{ 
                        width: column.width,
                        cursor: column.sortable ? 'pointer' : 'default',
                        color: '#262e35ff',
                        fontWeight: 600,
                        borderBottom: '1px solid #6c757d',
                        padding: '10px 8px'
                      }}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <div className="d-flex align-items-center">
                        {column.label}
                        {column.sortable && (
                          <FontAwesomeIcon 
                            icon={getSortIcon(column.key)} 
                            className="ms-1" 
                            style={{ fontSize: '12px' }}
                          />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item, index) => (
                  <tr 
                    key={item.positionId || item.kindergartenId || index} 
                    className={`${index % 2 === 0 ? '' : 'staff-table-even'} ${onRowClick && item.kindergartenId && isAggregatedView ? 'clickable-row' : ''}`}
                    onClick={() => isAggregatedView && handleRowClick(item)}
                  >
                    {columns.map((column) => (
                      <td key={column.key} style={{ padding: '10px 8px', borderBottom: '1px solid #e9ecef' }}>
                        {column.render ? column.render(item[column.key], item) : (
                          (column.key === 'vacantUnits' || column.key === 'vacantPositions') ? (
                            <span className={item.vacantUnits > 0 ? 'staff-vacant-highlight' : ''}>
                              {item[column.key]}
                            </span>
                          ) : (
                            item[column.key]
                          )
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="table-footer-note">
            Всього {isAggregatedView ? 'садочків' : 'посад'}: <strong>{sortedData.length}</strong> | 
            Загальна кількість штатних одиниць: <strong>{cor2Float(totalStats.totalPositions)}</strong> | 
            {!isAggregatedView && <>Працівників:   <strong>{totalStats.totalEmployees}</strong> |</>}  
            Вакантних позицій: <strong className={totalStats.totalVacant > 0 ? 'staff-vacant-highlight' : ''}>{cor2Float(totalStats.totalVacant)}</strong>
          </div>
        </>
      ) : (
        <div className="text-center py-5 text-muted">
          {searchTerm ? (
            'Не знайдено за вашим запитом'
          ) : data.length === 0 ? (
            isAggregatedView ? 'Оберіть район або садочок для перегляду штатного розкладу' : 'Немає даних про посади для обраного садочка'
          ) : (
            'Немає даних про штатний розпис для обраних параметрів'
          )}
        </div>
      )}
    </div>
  );
};

export default StaffTable;